<template>
    <div>
        <breadcrumb :items="breadcrumbs" />

        <v-container fluid v-if="showStep2">
            <div class="d-flex align-center justify-space-between mt-6 mb-4">
                <h2 class="font-weight-bold mb-0">{{ programData.programTitle }} - {{ $t('finance.loanPrograms.applicationForm') }}</h2>
                <v-btn color="primary" dark @click="showStep2 = false">
                    {{ $t('Back') }}
                </v-btn>
            </div>
            <v-card flat class="pa-6">
                <StepTwo :data-fields="programData.applicationFormFields" :readonly="false" />
            </v-card>
        </v-container>
        <v-container fluid v-else>
            <div class="d-flex align-center justify-space-between mt-6 mb-4">
                <h2 class="font-weight-bold mb-6">{{ programData.programTitle }}</h2>
                <div>
                    <v-btn color="primary" dark class="mr-2 mb-2" @click="editProgram">
                        {{ $t('Edit') }}
                    </v-btn>
                    <v-btn color="primary" dark class="mr-2 mb-2" @click="viewApplications">
                        {{ $t('finance.loanPrograms.viewApplications') }}
                    </v-btn>
                    <v-btn color="primary" dark class="mr-2 mb-2" @click="viewApplicationForm">
                        {{ $t('finance.loanPrograms.applicationForm') }}
                    </v-btn>
                    <v-btn color="primary" dark class="mr-2 mb-2" @click="$router.go(-1)">
                        {{ $t('Back') }}
                    </v-btn>
                </div>
            </div>

            <v-card flat class="pa-6">
                <!-- Program Details Section -->
                <v-row class="mb-6">
                    <v-col cols="12" md="3">
                        <div class="d-flex align-center">
                            <label class="view-label">{{ $t("finance.loanPrograms.programTitle") }}</label>
                            <v-tooltip top max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon class="ml-2 pb-2" size="20" style="color: #00BD73; cursor: pointer;"
                                        v-bind="attrs" v-on="on">
                                        mdi-alert-circle
                                    </v-icon>
                                </template>
                                <span>{{ $t("finance.loanPrograms.programTitle") }}</span>
                            </v-tooltip>
                        </div>
                        <div class="d-flex align-center mt-2">
                            <img :src="programData.programLogo" :alt="$t('finance.loanPrograms.programLogo')" class="program-logo mr-3"
                                v-if="programData.programLogo" />
                            <div>
                                <div class="view-value">{{ programData.programTitle }}</div>
                            </div>
                        </div>
                    </v-col>
                    <v-col cols="12" md="3">
                        <div class="d-flex align-center">
                            <label class="view-label">{{ $t("finance.loanPrograms.cooperative") }}</label>
                            <v-tooltip top color="00BD73" max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon class="ml-2 pb-2" size="20" style="color: #00BD73; cursor: pointer;"
                                        v-bind="attrs" v-on="on">
                                        mdi-alert-circle
                                    </v-icon>
                                </template>
                                <span>{{ $t("finance.loanPrograms.cooperative") }}</span>
                            </v-tooltip>
                        </div>
                        <div class="d-flex align-center mt-2">
                            <img
                                :src="cooperativeDisplayLogo"
                                :alt="cooperativeDisplayName"
                                class="bank-logo mr-3"
                                v-if="cooperativeDisplayLogo"
                            />
                            <div class="view-value">{{ cooperativeDisplayName }}</div>
                        </div>
                    </v-col>

                    <v-col cols="12" md="3">
                        <div class="d-flex align-center">
                            <label class="view-label">{{ $t("finance.loanPrograms.bank") }}</label>
                            <v-tooltip top color="00BD73" max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon class="ml-2 pb-2" size="20" style="color: #00BD73; cursor: pointer;"
                                        v-bind="attrs" v-on="on">
                                        mdi-alert-circle
                                    </v-icon>
                                </template>
                                <span>{{ $t("finance.loanPrograms.bank") }}</span>
                            </v-tooltip>
                        </div>
                        <div class="d-flex align-center mt-2">
                            <img :src="programData.bankLogo" :alt="$t('finance.banks.bankLogo')" class="bank-logo mr-3"
                                v-if="programData.bankLogo" />
                            <div class="view-value">{{ programData.bankName }}</div>
                        </div>
                    </v-col>

                    <v-col cols="12" md="3">
                        <div class="d-flex align-center">
                            <label class="view-label">{{ $t("finance.loanPrograms.status") }}</label>
                            <v-tooltip top max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon class="ml-2 pb-2" size="20" style="color: #00BD73; cursor: pointer;"
                                        v-bind="attrs" v-on="on">
                                        mdi-alert-circle
                                    </v-icon>
                                </template>
                                <span>{{ $t("finance.loanPrograms.status") }}</span>
                            </v-tooltip>
                        </div>
                        <div class="mt-2">
                            <v-chip small :color="programData.status === 'active' ? '#00BD73' : '#757575'"
                                text-color="white" class="font-weight-bold">
                                {{ programData.status === 'active' ? $t('finance.loanPrograms.active') : $t('finance.loanPrograms.inactive') }}
                            </v-chip>
                        </div>
                    </v-col>
                </v-row>

                <!-- Approval Flow setting  -->
                <v-row v-if="programData.applicationApproval === 'BANK'" class="mb-6">
                    <v-col cols="12" md="3">
                        <div class="d-flex align-center">
                            <label class="view-label">{{ $t("finance.loanPrograms.approvalFlowSetting") }}</label>
                            <v-tooltip top max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon class="ml-2 pb-2" size="20" style="color: #00BD73; cursor: pointer;"
                                        v-bind="attrs" v-on="on">
                                        mdi-alert-circle
                                    </v-icon>
                                </template>
                                <span>{{ $t("finance.loanPrograms.approvalFlowSetting") }}</span>
                            </v-tooltip>
                        </div>
                        <div class="view-value mt-2">{{ programData.approvalFlow }}</div>
                        <div class="d-flex align-center">
                            <label v-if="programData.settingsConclude" class="view-label">{{ $t('finance.loanPrograms.concludeBeforeBank') }}</label>
                            <label v-if="programData.settingsAllowBank" class="view-label">{{ $t('finance.loanPrograms.allowBankRequestInfo') }}</label>
                        </div>
                    </v-col>
                </v-row>

                <hr class="my-4">

                <v-row class="mb-6">
                    <v-col cols="12" md="3">
                        <div class="d-flex align-center">
                            <label class="view-label">{{ $t("finance.loanPrograms.currency") }}</label>
                            <v-tooltip top max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon class="ml-2 pb-2" size="20" style="color: #00BD73; cursor: pointer;"
                                        v-bind="attrs" v-on="on">
                                        mdi-alert-circle
                                    </v-icon>
                                </template>
                                <span>{{ $t("finance.loanPrograms.currency") }}</span>
                            </v-tooltip>
                        </div>
                        <div class="view-value mt-2">{{ programData.currency }}</div>
                    </v-col>
                    <v-col cols="12" md="3">
                        <div class="d-flex align-center">
                            <label class="view-label">{{ $t("finance.loanPrograms.loanAmountMin") }}</label>
                            <v-tooltip top max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon class="ml-2 pb-2" size="20" style="color: #00BD73; cursor: pointer;"
                                        v-bind="attrs" v-on="on">
                                        mdi-alert-circle
                                    </v-icon>
                                </template>
                                <span>{{ $t("finance.loanPrograms.loanAmountMin") }}</span>
                            </v-tooltip>
                        </div>
                        <div class="view-value mt-2">{{ programData.currency }} {{
                            formatNumber(programData.loanAmountMin) }}</div>
                    </v-col>

                    <v-col cols="12" md="3">
                        <div class="d-flex align-center">
                            <label class="view-label">{{ $t("finance.loanPrograms.loanAmountMax") }}</label>
                            <v-tooltip top max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon class="ml-2 pb-2" size="20" style="color: #00BD73; cursor: pointer;"
                                        v-bind="attrs" v-on="on">
                                        mdi-alert-circle
                                    </v-icon>
                                </template>
                                <span>{{ $t("finance.loanPrograms.loanAmountMax") }}</span>
                            </v-tooltip>
                        </div>
                        <div class="view-value mt-2">{{ programData.currency }} {{
                            formatNumber(programData.loanAmountMax) }}</div>
                    </v-col>

                    <v-col cols="12" md="3">
                        <div class="d-flex align-center">
                            <label class="view-label">{{ $t("finance.loanPrograms.term") }}</label>
                            <v-tooltip top max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon class="ml-2 pb-2" size="20" style="color: #00BD73; cursor: pointer;"
                                        v-bind="attrs" v-on="on">
                                        mdi-alert-circle
                                    </v-icon>
                                </template>
                                <span>{{ $t("finance.loanPrograms.term") }}</span>
                            </v-tooltip>
                        </div>
                        <div class="view-value mt-2">{{ programData.term }}</div>
                    </v-col>
                </v-row>

                <v-row class="mb-6">
                    <v-col cols="12" md="3">
                        <div class="d-flex align-center">
                            <label class="view-label">{{ $t("finance.loanPrograms.yield") }}</label>
                            <v-tooltip top max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon class="ml-2 pb-2" size="20" style="color: #00BD73; cursor: pointer;"
                                        v-bind="attrs" v-on="on">
                                        mdi-alert-circle
                                    </v-icon>
                                </template>
                                <span>{{ $t("finance.loanPrograms.yield") }}</span>
                            </v-tooltip>
                        </div>
                        <div class="view-value mt-2">{{ programData.yield }}</div>
                    </v-col>
                    <v-col cols="12" md="3">
                        <div class="d-flex align-center">
                            <label class="view-label">{{ $t("finance.loanPrograms.typeOfFinancing") }}</label>
                            <v-tooltip top max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon class="ml-2 pb-2" size="20" style="color: #00BD73; cursor: pointer;"
                                        v-bind="attrs" v-on="on">
                                        mdi-alert-circle
                                    </v-icon>
                                </template>
                                <span>{{ $t("finance.loanPrograms.typeOfFinancing") }}</span>
                            </v-tooltip>
                        </div>
                        <div class="view-value mt-2">{{ programData.typeOfFinancing }}</div>
                    </v-col>
                    <v-col cols="12" md="3">
                        <div class="d-flex align-center">
                            <label class="view-label">{{ $t("finance.loanPrograms.gracePeriod") }}</label>
                            <v-tooltip top max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon class="ml-2 pb-2" size="20" style="color: #00BD73; cursor: pointer;"
                                        v-bind="attrs" v-on="on">
                                        mdi-alert-circle
                                    </v-icon>
                                </template>
                                <span>{{ $t("finance.loanPrograms.gracePeriod") }}</span>
                            </v-tooltip>
                        </div>
                        <div class="view-value mt-2">{{ programData.gracePeriod }}</div>
                    </v-col>

                    <v-col cols="12" md="3">
                        <div class="d-flex align-center">
                            <label class="view-label">{{ $t("finance.loanPrograms.country") }}</label>
                            <v-tooltip top max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon class="ml-2 pb-2" size="20" style="color: #00BD73; cursor: pointer;"
                                        v-bind="attrs" v-on="on">
                                        mdi-alert-circle
                                    </v-icon>
                                </template>
                                <span>{{ $t("finance.loanPrograms.country") }}</span>
                            </v-tooltip>
                        </div>
                        <div class="view-value mt-2">{{ programData.country }}</div>
                    </v-col>
                </v-row>

                <v-row class="mb-6">
                    <v-col cols="12" md="3">
                        <div class="d-flex align-center">
                            <label class="view-label">{{ $t("finance.loanPrograms.regions") }}</label>
                            <v-tooltip top max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon class="ml-2 pb-2" size="20" style="color: #00BD73; cursor: pointer;"
                                        v-bind="attrs" v-on="on">
                                        mdi-alert-circle
                                    </v-icon>
                                </template>
                                <span>{{ $t("finance.loanPrograms.regions") }}</span>
                            </v-tooltip>
                        </div>
                        <div class="view-value mt-2">{{ programData.regions }}</div>
                    </v-col>
                    <v-col cols="12" md="3">
                        <div class="d-flex align-center">
                            <label class="view-label">{{ $t("finance.loanPrograms.crops") }}</label>
                            <v-tooltip top max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon class="ml-2 pb-2" size="20" style="color: #00BD73; cursor: pointer;"
                                        v-bind="attrs" v-on="on">
                                        mdi-alert-circle
                                    </v-icon>
                                </template>
                                <span>{{ $t("finance.loanPrograms.crops") }}</span>
                            </v-tooltip>
                        </div>
                        <div class="view-value mt-2">{{ programData.crops }}</div>
                    </v-col>
                </v-row>
                <!-- Data Fields (view-only grid) -->
                <template v-if="viewDataFields.length">
                    <hr class="my-4">
                    <div class="mb-6">
                        <div class="d-flex align-center mb-4">
                            <h3 class="font-weight-bold mr-2">{{ $t('finance.loanPrograms.dataFields') }}</h3>
                            <v-tooltip top max-width="300">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon size="20" style="color: #00BD73; cursor: pointer;" v-bind="attrs" v-on="on">
                                        mdi-alert-circle
                                    </v-icon>
                                </template>
                                <span>{{ $t('finance.loanPrograms.dataFieldsConfigured') }}</span>
                            </v-tooltip>
                        </div>

                        <div v-for="(section, sIdx) in viewDataFields" :key="sIdx" :class="{ 'px-4': sIdx > 0 }"
                            :style="section.isGroup ? 'padding: 10px; margin-top: 15px; border: 1px solid #e0e0e0; border-radius: 5px; background-color: #f9f9f9;' : ''">
                            <!-- Group header -->
                            <div v-if="section.isGroup" class="d-flex align-center mb-3">
                                <h4 class="font-weight-bold mr-2">{{ section.groupTitle }}</h4>
                                <v-tooltip v-if="section.groupHelpText" top max-width="300">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon size="18" style="color: #00BD73; cursor: pointer;" v-bind="attrs"
                                            v-on="on">
                                            mdi-alert-circle
                                        </v-icon>
                                    </template>
                                    <span>{{ section.groupHelpText }}</span>
                                </v-tooltip>
                            </div>

                            <v-row>
                                <v-col v-for="(field, fIdx) in section.fields" :key="fIdx">
                                    <div class="d-flex align-center">
                                        <label class="view-label">{{ field.title }}</label>
                                        <v-tooltip v-if="field.helpText" top max-width="300">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon class="ml-2 pb-2" size="20"
                                                    style="color: #00BD73; cursor: pointer;" v-bind="attrs" v-on="on">
                                                    mdi-alert-circle
                                                </v-icon>
                                            </template>
                                            <span>{{ field.helpText }}</span>
                                        </v-tooltip>
                                    </div>

                                    <div v-if="field.fieldType === 'attach_file' && getAttachFileUrls(field).length"
                                        class="d-flex flex-wrap mt-2" style="gap: 8px;">
                                        <a v-for="(url, imgIdx) in getAttachFileUrls(field)" :key="imgIdx" :href="url"
                                            target="_blank" rel="noopener">
                                            <img :src="url" :alt="field.fieldName || field.title"
                                                class="data-field-image" />
                                        </a>
                                    </div>

                                    <!-- digital_signature: data URL, or hosted image (e.g. S3 .png), or other file link -->
                                    <div
                                        v-else-if="isDigitalSignatureField(field) && isSignatureOrImageUrl(field.value)"
                                        class="mt-2 d-flex flex-column"
                                        style="gap: 8px;"
                                    >
                                        <a
                                            v-if="isLikelySignatureImageUrl(field.value)"
                                            :href="field.value"
                                            target="_blank"
                                            rel="noopener"
                                            class="data-field-signature-link"
                                        >
                                            <img
                                                :src="field.value"
                                                :alt="field.title || field.fieldName || $t('finance.loanPrograms.signature')"
                                                class="data-field-image"
                                            />
                                        </a>
                                        <a
                                            v-else
                                            :href="field.value"
                                            target="_blank"
                                            rel="noopener"
                                            class="view-value"
                                        >
                                            {{ $t('finance.loanPrograms.viewSignatureFile') }}
                                        </a>
                                        <div
                                            v-if="signatureFieldModeLabels(field).length"
                                            class="text-caption grey--text text--darken-1"
                                        >
                                            {{ signatureFieldModeLabels(field).join(' · ') }}
                                        </div>
                                    </div>
                                    <div v-else class="view-value mt-2">{{ formatDataFieldDisplay(field) }}</div>
                                </v-col>
                            </v-row>
                        </div>
                    </div>
                </template>

                <hr class="my-4">
                <!-- Description Section -->
                <div class="mb-6">
                    <div class="d-flex align-center mb-3">
                        <h3 class="font-weight-bold mr-2">{{ $t('finance.loanPrograms.description') }}</h3>
                        <v-tooltip top max-width="300">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon size="20" style="color: #00BD73; cursor: pointer;" v-bind="attrs" v-on="on">
                                    mdi-alert-circle
                                </v-icon>
                            </template>
                            <span>{{ $t('finance.loanPrograms.description') }}</span>
                        </v-tooltip>
                    </div>
                    <div class="description-text">{{ programData.description }}</div>
                </div>

                <!-- Conditions & Requirements Section -->
                <hr class="my-4" v-if="programData.conditionsRequirements">

                <div class="mb-6" v-if="programData.conditionsRequirements">
                    <div class="d-flex align-center mb-3">
                        <h3 class="font-weight-bold mr-2">{{ $t('finance.loanPrograms.conditionsRequirements') }}</h3>
                        <v-tooltip top max-width="300">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon size="20" style="color: #00BD73; cursor: pointer;" v-bind="attrs" v-on="on">
                                    mdi-alert-circle
                                </v-icon>
                            </template>
                            <span>{{ $t('finance.loanPrograms.conditionsRequirements') }}</span>
                        </v-tooltip>
                    </div>
                    <div class="description-text" v-html="programData.conditionsRequirements"></div>
                </div>

            </v-card>

        </v-container>
    </div>
</template>

<script>
import loadingMixin from '@/mixins/LoadingMixin';
import FinanceService from '@/_services/FinanceService';
import StepTwo from './components/StepTwo.vue';

export default {
    mixins: [loadingMixin],
    components: { StepTwo },
    computed: {
        viewDataFields() {
            return this.transformDataFields(this.programData.dataFields || []);
        },
        /** Logged-in cooperative org (from auth / same payload as localStorage user.user_organization) */
        cooperativeOrganization() {
            return this.$store.getters.getUser?.user_organization || null;
        },
        cooperativeDisplayName() {
            const name = this.cooperativeOrganization?.name;
            return name != null && String(name).trim() !== '' ? name : this.$t('finance.banks.notAvailable');
        },
        cooperativeDisplayLogo() {
            const logo = this.cooperativeOrganization?.logo;
            return logo != null && String(logo).trim() !== '' ? logo : '';
        },
    },
    data() {
        return {
            programId: null,
            breadcrumbs: [
                {
                    text: this.$t("Finance Management"),
                    disabled: false,
                    href: "/finance-management",
                    exact: true,
                },
                {
                    text: this.$t("finance.loanPrograms.title"),
                    disabled: false,
                    href: "/finance-management/loan-programs",
                },
                {
                    text: this.$t("finance.loanPrograms.details"),
                    disabled: true,
                },
            ],
            programData: {
                programTitle: '',
                programLogo: '',
                bankName: '',
                bankLogo: '',
                status: '',
                currency: '',
                loanAmountMin: 0,
                loanAmountMax: 0,
                yield: '',
                typeOfFinancing: '',
                gracePeriod: '',
                country: '',
                regions: '',
                term: '',
                crops: '',
                description: '',
                conditionsRequirements: '',
                dataFields: [],
                applicationFormFields: [],
            },
            showStep2: false,
        };
    },
    async mounted() {
        await this.loadProgramData();
    },
    methods: {
        getAttachFileUrls(field) {
            const v = field?.value;
            if (Array.isArray(v)) return v.filter(Boolean);
            return v ? [v] : [];
        },
        normalizedFieldType(field) {
            return String(field?.fieldType || '').replace(/-/g, '_');
        },
        isDigitalSignatureField(field) {
            return this.normalizedFieldType(field) === 'digital_signature';
        },
        isSignatureOrImageUrl(val) {
            if (val == null || val === '') return false;
            const s = String(val).trim();
            return (
                /^https?:\/\//i.test(s) ||
                s.startsWith('data:') ||
                s.startsWith('/')
            );
        },
        isLikelySignatureImageUrl(val) {
            if (!this.isSignatureOrImageUrl(val)) return false;
            const s = String(val).trim();
            if (/\.pdf(\?|#|$)/i.test(s)) return false;
            if (s.toLowerCase().startsWith('data:image')) return true;
            if (/^https?:\/\//i.test(s) || s.startsWith('/')) {
                return /\.(png|jpe?g|gif|webp|svg|bmp)(\?|#|$)/i.test(s);
            }
            return false;
        },
        signatureFieldModeLabels(field) {
            const o = field?.options;
            if (!o || typeof o !== 'object') return [];
            const parts = [];
            if (o.allowDigitalSignature === true) {
                parts.push(this.$t('finance.loanPrograms.digitalSignature'));
            }
            if (o.allowFileUpload === true) {
                parts.push(this.$t('finance.loanPrograms.fileUpload'));
            }
            return parts;
        },
        formatDataFieldDisplay(field) {
            const t = String(field.fieldType || '').replace(/-/g, '_');
            let v = field.value;

            if (v === undefined || v === null || v === '') {
                return this.$t('finance.banks.notAvailable');
            }

            if (t === 'digital_signature' && this.isSignatureOrImageUrl(v)) {
                return this.$t('finance.banks.notAvailable');
            }

            if (t === 'measurement_unit') {
                if (v && typeof v === 'object' && !Array.isArray(v) && ('amount' in v || 'unit' in v)) {
                    const amt = v.amount;
                    const u = v.unit != null ? String(v.unit).trim() : '';
                    if (amt !== undefined && amt !== null && amt !== '') {
                        return u ? `${amt} ${u}` : String(amt);
                    }
                    return u || this.$t('finance.banks.notAvailable');
                }
                if (typeof v === 'string' && v.trim().startsWith('{')) {
                    try {
                        const o = JSON.parse(v);
                        if (o && typeof o === 'object') {
                            const amt = o.amount;
                            const u = o.unit != null ? String(o.unit).trim() : '';
                            if (amt !== undefined && amt !== null && amt !== '') {
                                return u ? `${amt} ${u}` : String(amt);
                            }
                        }
                    } catch (_e) { /* ignore */ }
                }
            }

            if (t === 'check_boxes' && Array.isArray(v)) {
                return v.map((x) => String(x).trim()).filter(Boolean).join(', ') || this.$t('finance.banks.notAvailable');
            }

            if ((t === 'text_area' || t === 'text_field') && Array.isArray(v)) {
                return v.map((x) => String(x).trim()).join(', ') || this.$t('finance.banks.notAvailable');
            }

            if (Array.isArray(v)) {
                return v.map((x) => String(x).trim()).filter(Boolean).join(', ') || this.$t('finance.banks.notAvailable');
            }

            if (typeof v === 'object') {
                return JSON.stringify(v);
            }

            return String(v);
        },
        async loadProgramData() {
            this.programId = this.$route.params.id;

            if (this.programId) {
                try {
                    this.startLoading();
                    const response = await FinanceService.getLoanProgramById(this.programId);
                    const d = response.data;
                    if (d) {
                        this.programData = {
                            programTitle: d.programTitle || '',
                            programLogo: d.programLogo || '',
                            bankName: d.bank?.name || this.$t('finance.banks.notAvailable'),
                            bankLogo: d.bank?.logo || '',
                            status: d.status || '',
                            currency: d.loanCurrency || '',
                            loanAmountMin: d.loanMinAmt || 0,
                            loanAmountMax: d.loanMaxAmt || 0,
                            yield: d.loanYield != null ? `${d.loanYield}%` : this.$t('finance.banks.notAvailable'),
                            typeOfFinancing: d.loanFinancingType || this.$t('finance.banks.notAvailable'),
                            gracePeriod: this.formatTermUnit(d.loanGracePeriod, d.loanGracePeriodUnit),
                            country: d.loanCountry || this.$t('finance.banks.notAvailable'),
                            regions: d.loanRegion || this.$t('finance.banks.notAvailable'),
                            term: this.formatTermUnit(d.loanTerm, d.loanTermUnit),
                            crops: d.loanCrops || this.$t('finance.banks.notAvailable'),
                            description: d.description || '',
                            conditionsRequirements: d.conditionsRequirements || '',
                            dataFields: d.dataFields || [],
                            applicationFormFields: this.transformFields(d.applicationFormFields || []),
                        };
                    }
                } catch (err) {
                    console.error('Failed to load loan program:', err);
                    this.$notify({ title: this.$t('finance.loanPrograms.failedLoadLoanProgramDetails'), type: 'error' });
                } finally {
                    this.stopLoading();
                }
            }
        },
        formatNumber(num) {
            if (num == null) return '0';
            return new Intl.NumberFormat().format(num);
        },
        formatTermUnit(value, unit) {
            if (value == null) return this.$t('finance.banks.notAvailable');
            const unitMap = {
                m: this.$t('finance.loanPrograms.months'),
                y: this.$t('finance.loanPrograms.years'),
                d: this.$t('finance.loanPrograms.days'),
                w: this.$t('finance.loanPrograms.weeks'),
            };
            return `${value} ${unitMap[unit] || unit || ''}`.trim();
        },
        transformDataFields(fields) {
            if (!fields || !fields.length) return [];

            const mapField = (f) => ({
                title: f.fieldName,
                fieldName: f.fieldName,
                helpText: f.helpText || '',
                value: f.value,
                fieldType: f.fieldType,
                options: f.options && typeof f.options === 'object' ? { ...f.options } : null,
            });

            const ungrouped = [];
            const groupMap = {};

            for (const field of fields) {
                if (field.groupId && field.group) {
                    if (!groupMap[field.groupId]) {
                        groupMap[field.groupId] = {
                            isGroup: true,
                            groupTitle: field.group.groupName,
                            groupHelpText: field.group.helpText || '',
                            fields: [],
                        };
                    }
                    groupMap[field.groupId].fields.push(mapField(field));
                } else {
                    ungrouped.push(mapField(field));
                }
            }

            const sections = [];
            if (ungrouped.length) {
                sections.push({ isGroup: false, fields: ungrouped });
            }
            for (const group of Object.values(groupMap)) {
                sections.push(group);
            }
            return sections;
        },
        transformFields(fields) {
            if (!fields || !fields.length) return [];

            const apiTypeToStepType = (t) => t || '';
            const ungrouped = [];
            const groupMap = {};

            for (const field of fields) {
                const mapped = {
                    _uid: `field-${field.id}`,
                    fieldTitle: field.fieldName,
                    dataType: apiTypeToStepType(field.fieldType),
                    mandatory: !!field.isMandetory,
                    isGroup: false,
                    options: field.options || null,
                    value: field.value || null,
                    helpText: field.helpText || '',
                };

                if (field.groupId && field.group) {
                    if (!groupMap[field.groupId]) {
                        groupMap[field.groupId] = {
                            _uid: `group-${field.groupId}`,
                            isGroup: true,
                            groupTitle: field.group.groupName,
                            mandatory: false,
                            groupFields: [],
                        };
                    }
                    groupMap[field.groupId].groupFields.push(mapped);
                } else {
                    ungrouped.push(mapped);
                }
            }

            return [...ungrouped, ...Object.values(groupMap)];
        },
        editProgram() {
            this.$router.push({
                name: 'editLoanProgram',
                params: { id: this.programId }
            });
        },
        viewApplications() {
            this.$router.push({
                name: 'LoanApplication'
            });
        },
        viewApplicationForm() {
            this.$router.push({
                name: 'applicationFormView',
                params: { id: this.programId }
            });
        }
    }
};
</script>

<style scoped>
.view-label {
    font-size: 14px;
    color: #555;
    margin-bottom: 4px;
    display: block;
    font-weight: 400;
}

.view-value {
    font-weight: bold;
    font-size: 1rem;
    color: #333;
}

.view-subtitle {
    font-size: 0.875rem;
    color: #666;
    margin-top: 4px;
}

.program-logo,
.bank-logo {
    width: 40px;
    height: 40px;
    border-radius: 2px;
    object-fit: cover;
}

.data-field-image {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
    transition: opacity 0.2s;
}

.data-field-image:hover {
    opacity: 0.8;
}

.data-field-signature-thumb {
    max-width: 220px;
    max-height: 120px;
    object-fit: contain;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    display: block;
}

.data-field-signature-link {
    display: inline-block;
    text-decoration: none;
}

.data-field-signature-link:hover .data-field-signature-thumb {
    opacity: 0.9;
}

.description-text {
    font-size: 1rem;
    color: #333;
    line-height: 1.6;
}

.conditions-subtitle {
    font-size: 1rem;
    color: #333;
    margin-top: 1rem;
}

.conditions-list,
.requirements-list {
    padding-left: 1.5rem;
    color: #333;
    line-height: 1.8;
}

.conditions-list li,
.requirements-list li {
    margin-bottom: 0.5rem;
    font-size: 0.95rem;
}

.requirements-list {
    list-style-type: disc;
}

.conditions-list {
    list-style-type: disc;
}

.conditions-list ol {
    list-style-type: decimal;
    padding-left: 1.5rem;
}
</style>